import React from 'react'
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Button,
  Box,
  Paper,
  WithStyles,
  createStyles,
  Theme,
} from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/Delete'
import { withStyles } from '@material-ui/styles'
import { SiteCommentResponseModel } from 'api/apiservice'

interface Props {
  comments: SiteCommentResponseModel[]
  userEmail: string
  loading: boolean
  onEdit: (comment: SiteCommentResponseModel) => void
  onDelete: (comment: SiteCommentResponseModel) => void
  onAdd: () => void
  onShowMore: () => void
  ignoreLimit?: boolean
}

const styles = (theme: Theme) =>
  createStyles({
    value: {
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(0.5),
      width: 'auto',
      height: 'auto',
    },
  })

type AllProps = Props & WithStyles<typeof styles>

const CommentsTable: React.FC<AllProps> = ({
  comments,
  loading,
  onEdit,
  onDelete,
  onAdd,
  onShowMore,
  classes,
  userEmail,
  ignoreLimit = false,
}) => {
  const hasComments = comments.length > 0

  const sortedComments = comments
    .slice()
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )

  return (
    <div style={{ display: 'grid', overflow: 'hidden' }}>
      <Paper
        elevation={6}
        square
        style={{
          overflow: 'hidden',
          margin: 8,
          padding: 8,
        }}
      >
        {!ignoreLimit && (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">Comments</Typography>
            <Button variant="contained" color="primary" onClick={onAdd}>
              Add Comment
            </Button>
          </Box>
        )}

        <div
          style={{
            maxHeight: ignoreLimit ? 500 : 1000,
            overflowY: 'auto',
            transition: 'max-height 0.3s ease-in-out',
          }}
        >
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height={100}
            >
              <CircularProgress />
            </Box>
          ) : hasComments ? (
            <>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {['User Email', 'Comment', 'Date', 'Actions'].map(
                      (header, idx) => (
                        <TableCell key={idx} align="left">
                          <Typography
                            variant="subtitle2"
                            style={{
                              fontWeight: 'bold',
                              textTransform: 'uppercase',
                            }}
                          >
                            {header}
                          </Typography>
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(ignoreLimit
                    ? sortedComments
                    : sortedComments.slice(0, 3)
                  ).map((comment, index) => (
                    <TableRow key={index}>
                      <TableCell align="left">{comment.userEmail}</TableCell>
                      <TableCell align="left">{comment.comment}</TableCell>
                      <TableCell align="left">
                        {new Date(comment.createdAt)
                          .toLocaleString('en-US', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false,
                          })
                          .replace(' ', ', ')}
                      </TableCell>
                      {comment.userEmail?.toLowerCase() ===
                      userEmail.toLowerCase() ? (
                        <TableCell align="left">
                          <Tooltip title="Edit" arrow>
                            <IconButton
                              style={{ minWidth: 'auto' }}
                              color="primary"
                              size="small"
                              onClick={() => onEdit(comment)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete" arrow>
                            <IconButton
                              style={{ minWidth: 'auto' }}
                              color="primary"
                              size="small"
                              onClick={() => onDelete(comment)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      ) : (
                        <TableCell align="left" />
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {!ignoreLimit && sortedComments.length > 3 && (
                <Box display="flex" justifyContent="center" my={2}>
                  <Button variant="outlined" onClick={onShowMore}>
                    Expand all comments
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height={100}
            >
              <Typography variant="body1" color="textSecondary">
                No comments yet
              </Typography>
            </Box>
          )}
        </div>
      </Paper>
    </div>
  )
}

export default withStyles(styles)(CommentsTable)
